import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import type { PieCompositionCapabilities, PieCompositionVariant } from '../../../chart-components/unified-pie-composition-chart/unified-pie-composition-chart.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { FormatControl, FormatControlGroup, PieCompositionModeOption, PieVariantOption } from '../../shared/self-service-config.types';
import { extractLineAreaFlatRows } from '../../shared/self-service-recompute.utils';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { applyPieCompositionFormatToCapabilities } from './pie-composition-capabilities.utils';

/**
 * Pie & Composition build form (PIE_COMPOSITION): Group toggle (Standard /
 * Hierarchy / Progress) + variant (Pie / Donut / Semi Donut / Sunburst /
 * Treemap / Nested Donut / Radial / Circular / Ring Progress), Category +
 * Value pickers, an optional Parent / outer-group column (required by the
 * Hierarchy variants, or by Multiple Categories small-multiples), an optional
 * Color-by measure, and its OWN config-driven Format accordion (`pcFormat`,
 * separate from the shell's `barFormat`). NO X/Y axes anywhere. Renders
 * client-side from the cached dataset rows, so the tile is previewed live
 * while the form is edited; Generate also POSTs for server-side rows.
 */
@Component({
  selector: 'app-pie-composition-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './pie-composition-step.component.html',
  styles: [':host { display: contents; }'],
})
export class PieCompositionStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['PIE_COMPOSITION'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  /** Active side of the Group segmented toggle (a `pieCompositionModes` value). */
  pcMode = 'standard';
  pcVariant: PieCompositionVariant = 'pie';
  pcCategoryField = '';
  pcValueField = '';
  pcValueAggregation = 'SUM';
  pcParentField = '';
  pcColorField = '';
  pcMultipleCategories = false;
  pcDrillDownEnabled = true;
  pcColorByMeasure = false;
  /** Legend / Labels / … / Advanced options chosen in the Build form. */
  pcFormat: Record<string, unknown> = {};
  openPcFormatGroups = new Set<string>();
  pcDataAccordionOpen = true;

  // ── Variants & modes (config-driven, stable fallback instances) ──────────

  private readonly pieCompositionVariantsFallback: PieVariantOption[] = [
    { label: 'Pie', value: 'pie', mode: 'standard', group: 'Standard' },
    { label: 'Donut', value: 'donut', mode: 'standard', group: 'Standard', donut: true },
    { label: 'Semi Donut', value: 'semi-donut', mode: 'standard', group: 'Standard', donut: true, semiDonut: true },
    { label: 'Sunburst', value: 'sunburst', mode: 'hierarchy', group: 'Hierarchy', sunburst: true, usesParent: true },
    { label: 'Treemap', value: 'treemap', mode: 'hierarchy', group: 'Hierarchy', treemap: true, usesParent: true },
    { label: 'Nested Donut', value: 'nested-donut', mode: 'hierarchy', group: 'Hierarchy', donut: true, nestedDonut: true, usesParent: true },
    { label: 'Radial Progress', value: 'radial-progress', mode: 'progress', group: 'Progress', gauge: true, radialProgress: true },
    { label: 'Circular Progress', value: 'circular-progress', mode: 'progress', group: 'Progress', gauge: true, circularProgress: true },
    { label: 'Ring Progress', value: 'ring-progress', mode: 'progress', group: 'Progress', gauge: true, ringProgress: true },
  ];
  get pieCompositionVariants(): PieVariantOption[] {
    return this.host.layout.options?.pieCompositionVariants?.length
      ? (this.host.layout.options.pieCompositionVariants as PieVariantOption[])
      : this.pieCompositionVariantsFallback;
  }
  private readonly pcModesFallback: PieCompositionModeOption[] = [
    { label: 'Standard', value: 'standard', icon: 'pie_chart' },
    { label: 'Hierarchy', value: 'hierarchy', icon: 'account_tree' },
    { label: 'Progress', value: 'progress', icon: 'donut_large' },
  ];
  get pcModes(): PieCompositionModeOption[] {
    return this.host.layout.options?.pieCompositionModes?.length
      ? (this.host.layout.options.pieCompositionModes as PieCompositionModeOption[])
      : this.pcModesFallback;
  }
  private get pcDefaultMode(): string {
    return this.pcModes[0]?.value ?? 'standard';
  }
  private pcVariantModeOf(v: PieVariantOption): string {
    return v.mode ?? this.pcDefaultMode;
  }
  /** Variant dropdown items — MEMOIZED (a fresh array per CD cycle breaks ng-select appendTo:'body'). */
  private pcVariantItemsCache: PieVariantOption[] = [];
  private pcVariantItemsCacheKey: { source: PieVariantOption[]; mode: string } | null = null;
  get pcVariantItems(): PieVariantOption[] {
    const source = this.pieCompositionVariants;
    if (this.pcVariantItemsCacheKey?.source !== source || this.pcVariantItemsCacheKey.mode !== this.pcMode) {
      this.pcVariantItemsCache = source.filter((v) => this.pcVariantModeOf(v) === this.pcMode);
      this.pcVariantItemsCacheKey = { source, mode: this.pcMode };
    }
    return this.pcVariantItemsCache;
  }
  setPcMode(mode: string): void {
    if (this.pcMode === mode) return;
    this.pcMode = mode;
    if (!this.pcVariantItems.some((v) => v.value === this.pcVariant)) {
      this.pcVariant = (this.pcVariantItems[0]?.value ?? 'pie') as PieCompositionVariant;
    }
    this.onPieCompositionChange();
  }
  private pcModeForVariant(variant: string): string {
    const entry = this.pieCompositionVariants.find((v) => v.value === variant);
    return entry ? this.pcVariantModeOf(entry) : this.pcDefaultMode;
  }
  private get pcVariantEntry(): PieVariantOption | undefined {
    return this.pieCompositionVariants.find((v) => v.value === this.pcVariant);
  }
  /** Hierarchy variants always need the Parent / outer-group column. */
  get pcVariantUsesParent(): boolean {
    return this.pcVariantEntry?.usesParent === true;
  }
  get pcVariantIsGauge(): boolean {
    return this.pcVariantEntry?.['gauge'] === true;
  }
  get pcVariantHint(): string {
    return this.pcVariantEntry?.hint || '';
  }
  /** Parent column is required (Hierarchy) or needed for small multiples. */
  get pcNeedsParent(): boolean {
    return this.pcVariantUsesParent || this.pcMultipleCategories;
  }

  // ── Format accordion (own pcFormat state) ────────────────────────────────

  private pcFormatGroupsCache: FormatControlGroup[] | null = null;
  private pcFormatGroupsCacheType = '';
  get pieCompositionFormatGroups(): FormatControlGroup[] {
    if (this.pcFormatGroupsCache && this.pcFormatGroupsCacheType === this.host.selectedLayoutType) {
      return this.pcFormatGroupsCache;
    }
    const applicable = this.host.applicableFormatControls(this.host.selectedLayoutType);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) { byGroup.set(group, []); seen.push(group); }
      byGroup.get(group)!.push(control);
    });
    const order = this.host.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    const exposed = seen.filter((name) => this.host.chartExposesSection(name));
    this.pcFormatGroupsCacheType = this.host.selectedLayoutType;
    this.pcFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.pcFormatGroupsCache;
  }
  isPcFormatGroupOpen(group: string): boolean {
    return this.openPcFormatGroups.has(group);
  }
  togglePcFormatGroup(group: string): void {
    const next = new Set(this.openPcFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openPcFormatGroups = next;
  }
  private seedPieCompositionFormatDefaults(): void {
    this.host.applicableFormatControls('PIE_COMPOSITION').forEach((control) => {
      if (control.default !== undefined && this.pcFormat[control.key] === undefined) {
        this.pcFormat[control.key] = control.default;
      }
    });
  }

  /** Column list for the field pickers — MEMOIZED for ng-select appendTo:'body'. */
  private pcColumnsCache: string[] = [];
  private pcColumnsKey = '';
  get pieCompositionColumns(): string[] {
    const base = this.host.drillDatasetColumns;
    const key = base.join('|');
    if (key !== this.pcColumnsKey) {
      this.pcColumnsKey = key;
      this.pcColumnsCache = [...base];
    }
    return this.pcColumnsCache;
  }

  // ── Field drops ──────────────────────────────────────────────────────────

  onPcCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.pcCategoryField = mapping.field;
    this.onPieCompositionChange();
  }
  onPcValueFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.pcValueField = mapping.field;
    this.onPieCompositionChange();
  }
  onPcParentFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.pcParentField = mapping.field;
    this.onPieCompositionChange();
  }
  onPcColorFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.pcColorField = mapping.field;
    this.onPieCompositionChange();
  }

  // ── Live preview ─────────────────────────────────────────────────────────

  onPieCompositionChange(): void {
    this.host.invalidatePreview();
    this.refreshEditingPieCompositionTile();
  }
  onPieCompositionFormatChange(): void {
    if (typeof this.pcFormat['title'] === 'string') this.host.title = this.pcFormat['title'] as string;
    this.refreshEditingPieCompositionTile();
  }
  private refreshEditingPieCompositionTile(): void {
    if (!this.active) return;
    if (this.host.editingTileId) {
      const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
      const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'PIE_COMPOSITION') {
        this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? this.rebuildPieCompositionTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewPieComposition) {
      this.host.removePreviewTile();
      return;
    }
    const existingIndex = this.host.previewTileId ? this.host.dashboardTiles.findIndex((t) => t.id === this.host.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.host.newTileBase(this.host.buildPayload());
      this.host.previewTileId = base.id;
      const draft = this.rebuildPieCompositionTile({ ...base, layoutType: 'PIE_COMPOSITION' });
      this.host.dashboardTiles = [...this.host.dashboardTiles, draft];
    } else {
      const tile = this.host.dashboardTiles[existingIndex];
      this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildPieCompositionTile(tile) : t));
    }
  }
  private rebuildPieCompositionTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.host.defaultTileFormat('PIE_COMPOSITION'), ...this.pcFormat, title: this.host.title.trim() || undefined };
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return {
      ...tile,
      title: this.host.title.trim() || tile.title || 'Untitled',
      format,
      pieCompositionRows: rows,
      pieCompositionData: this.buildPieCompositionWidgetData(tile.id, format),
    };
  }

  get canPreviewPieComposition(): boolean {
    if (!this.active || !this.host.drillDataset) return false;
    if (!this.host.cachedRows(this.host.drillDataset)?.length) return false;
    if (!this.pcCategoryField || !this.pcValueField) return false;
    if (this.pcNeedsParent && !this.pcParentField) return false;
    return true;
  }

  // ── Capabilities / payload ───────────────────────────────────────────────

  /** Non-flag keys on a config variant entry; every OTHER `true` key passes
   *  through to `variantFlags` generically (a new JSON flag needs no code). */
  private static readonly PC_NON_FLAG_KEYS = new Set(['label', 'value', 'mode', 'group', 'hint', 'icon', 'usesParent']);

  private buildPieCompositionCapabilities(): PieCompositionCapabilities {
    const entry = this.pcVariantEntry;
    const variantFlags: Record<string, boolean> = {};
    if (entry) {
      Object.entries(entry as Record<string, unknown>).forEach(([key, val]) => {
        if (val === true && !PieCompositionStepComponent.PC_NON_FLAG_KEYS.has(key)) variantFlags[key] = true;
      });
    }
    return {
      variant: this.pcVariant,
      categoryField: this.pcCategoryField,
      valueField: this.pcValueField,
      parentField: this.pcNeedsParent && this.pcParentField ? this.pcParentField : undefined,
      colorField: this.pcColorByMeasure && this.pcColorField ? this.pcColorField : undefined,
      multipleCategories: this.pcMultipleCategories,
      drillDownEnabled: this.pcDrillDownEnabled,
      colorByMeasure: this.pcColorByMeasure,
      ...(this.host.layout.options?.pieCompositionChartDefaults
        ? { chartDefaults: this.host.layout.options.pieCompositionChartDefaults as PieCompositionCapabilities['chartDefaults'] }
        : {}),
      variantFlags,
    } as PieCompositionCapabilities;
  }

  /** Backend column contract (same shape as the Scatter & Bubble payload). */
  private buildPieCompositionColumns(capabilities: PieCompositionCapabilities): Array<Record<string, unknown>> {
    const dataset = this.host.drillDataset;
    const seen = new Set<string>();
    const columns: Array<Record<string, unknown>> = [];
    const push = (raw: string | undefined, aggregation?: string): void => {
      const field = String(raw || '').trim();
      if (!field || seen.has(field)) return;
      seen.add(field);
      const agg = aggregation && aggregation !== 'NONE' ? aggregation : undefined;
      columns.push({ dataset, field, displayName: field, ...(agg ? { aggregation: agg } : {}) });
    };
    push(capabilities.categoryField);
    push(capabilities.parentField);
    push(capabilities.valueField, this.pcValueAggregation || 'SUM');
    push(capabilities.colorField, 'AVG');
    return columns;
  }

  private buildPieCompositionWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const capabilities = applyPieCompositionFormatToCapabilities(this.buildPieCompositionCapabilities(), format, (key) => this.host.resolvePaletteColors(key));
    return {
      widgetId: 'dvm-tile-pc-' + tileId,
      title: this.host.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  private buildPieCompositionTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    base.format = { ...this.host.defaultTileFormat('PIE_COMPOSITION'), ...this.pcFormat, title: this.host.title.trim() || undefined };
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return { ...base, pieCompositionRows: rows, pieCompositionData: this.buildPieCompositionWidgetData(base.id, base.format) };
  }

  /** Server rows replace the client rows when they carry the category field. */
  private applyPieCompositionComputeResponse(payload: Record<string, unknown>, response: Record<string, unknown> | null): void {
    const rows = extractLineAreaFlatRows(response?.['responseList']) ?? extractLineAreaFlatRows(response);
    if (!rows?.length) return;
    const componentId = String(payload['componentId'] ?? '');
    if (!componentId) return;
    const apply = (t: DashboardTile): DashboardTile => {
      if (t.componentId !== componentId || !t.pieCompositionData) return t;
      if (!this.host.rowsFitClientChart(rows, t.pieCompositionData, 'categoryField')) return t;
      return { ...t, pieCompositionRows: rows, pieCompositionData: { ...t.pieCompositionData, staticData: rows } };
    };
    this.host.dashboardTiles = this.host.dashboardTiles.map((t) => {
      const updated = apply(t);
      if (updated !== t) return updated;
      if (!t.groupMembers?.length) return t;
      const members = t.groupMembers.map(apply);
      return members.some((m, i) => m !== t.groupMembers![i]) ? { ...t, groupMembers: members } : t;
    });
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.pcCategoryField) missing.push('Category column');
    if (!this.pcValueField) missing.push('Value column');
    if (this.pcNeedsParent && !this.pcParentField) missing.push('Parent / outer group column');
    if (this.pcColorByMeasure && !this.pcColorField) missing.push('Color-by measure column');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const capabilities = this.buildPieCompositionCapabilities();
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'PIE_COMPOSITION'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      columns: this.buildPieCompositionColumns(capabilities),
      capabilities: capabilities as unknown as Record<string, unknown>,
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
      ?? ((tile.pieCompositionData?.['capabilities'] as Record<string, unknown> | undefined))
      ?? {};
    const savedVariant = String(caps['variant'] ?? 'pie');
    const known = this.pieCompositionVariants.some((v) => v.value === savedVariant);
    this.pcVariant = (known ? savedVariant : 'pie') as PieCompositionVariant;
    this.pcMode = this.pcModeForVariant(this.pcVariant);
    this.pcCategoryField = String(caps['categoryField'] ?? '');
    this.pcValueField = String(caps['valueField'] ?? '');
    this.pcParentField = String(caps['parentField'] ?? '');
    this.pcColorField = String(caps['colorField'] ?? '');
    this.pcMultipleCategories = caps['multipleCategories'] === true;
    this.pcDrillDownEnabled = caps['drillDownEnabled'] !== false;
    this.pcColorByMeasure = caps['colorByMeasure'] === true;
    const columns = Array.isArray(payload['columns']) ? (payload['columns'] as Array<Record<string, unknown>>) : [];
    const valueColumn = columns.find((c) => String(c['field'] ?? '') === this.pcValueField);
    this.pcValueAggregation = String(valueColumn?.['aggregation'] ?? 'SUM');
    this.pcFormat = { ...(tile.format || {}) };
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.pcMode = this.pcDefaultMode;
    this.pcVariant = (this.pcVariantItems[0]?.value ?? 'pie') as PieCompositionVariant;
    this.pcCategoryField = '';
    this.pcValueField = '';
    this.pcValueAggregation = 'SUM';
    this.pcParentField = '';
    this.pcColorField = '';
    this.pcMultipleCategories = false;
    this.pcDrillDownEnabled = true;
    this.pcColorByMeasure = false;
    this.pcFormat = {};
  }

  /** Seed the Format accordion defaults whenever this family becomes active. */
  onLayoutActivated(): void {
    this.seedPieCompositionFormatDefaults();
  }

  onDatasetRowsLoaded(): void {
    this.refreshEditingPieCompositionTile();
  }

  /** Promotes the live preview tile (or adds a new one), then POSTs for server-computed rows. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    if (this.host.previewTileId && this.host.dashboardTiles.some((t) => t.id === this.host.previewTileId)) {
      const id = this.host.previewTileId;
      this.host.dashboardTiles = this.host.dashboardTiles.map((t) =>
        t.id === id ? { ...this.rebuildPieCompositionTile(t), componentId: this.host.componentId, payload } : t,
      );
      this.host.previewTileId = null;
    } else {
      this.host.addTile(this.buildPieCompositionTile(payload));
    }
    (this.host.requestGenerate?.(url, payload) ?? this.http.post<Record<string, unknown>>(url, payload)).subscribe({
      next: (response) => {
        this.applyPieCompositionComputeResponse(payload, response);
        this.ui.showSuccess('Pie / composition chart added to the dashboard.');
      },
      error: () => this.ui.showError('Pie / composition chart added, but saving its configuration failed.'),
    });
    return true;
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.pieCompositionData) return;
    const capabilities = applyPieCompositionFormatToCapabilities(
      { ...(tile.pieCompositionData['capabilities'] as unknown as PieCompositionCapabilities) },
      format,
      (key) => this.host.resolvePaletteColors(key),
    );
    tile.pieCompositionData = {
      ...tile.pieCompositionData,
      title: (format['title'] as string) || tile.title,
      capabilities: capabilities as unknown as Record<string, unknown>,
    };
  }

  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const rows = (Array.isArray(result['data'])
      ? result['data']
      : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
    if (tile.pieCompositionData && this.host.rowsFitClientChart(rows, tile.pieCompositionData, 'categoryField')) {
      tile.pieCompositionData = { ...tile.pieCompositionData, staticData: Array.isArray(rows) ? rows : [] };
      return true;
    }
    return false;
  }
}
